import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import {useAxiosPrivate} from "../useAxiosPrivate";
import {useContext} from "react";
import {AuthContext} from "../../context/AuthContext";
import {hasPermission} from "../../utils/permissionUtils";

export default function useBusinessQueries() {
    const axiosPrivate = useAxiosPrivate();
    const queryClient = useQueryClient();
    const {isAuthenticated, getUser} = useContext(AuthContext);
    const user = getUser()

    // QUERIES
    const businessGetBusinessQuery = useQuery({
        queryKey: ["business", "get_business"],
        queryFn: () => axiosPrivate.get("business/get_business/").then(r => r.data),
        enabled: isAuthenticated
    });

    const businessGetSubscriptionInfoQuery = useQuery({
        queryKey: ["business", "get-subscription-info"],
        queryFn: () => axiosPrivate.get("business/get-subscription-info/").then(r => r.data),
        enabled: isAuthenticated
    });

    const businessGetServiceStatsQuery = useQuery({
        queryKey: ["business", "get_service_stats"],
        queryFn: () => axiosPrivate.get("business/get_service_stats/").then(r => r.data),
        enabled: isAuthenticated && hasPermission(user, 'stats', 'view')
    });

    const businessGetReservationStatsQuery = useQuery({
        queryKey: ["business", "get_reservation_stats"],
        queryFn: () => axiosPrivate.get("business/get_reservation_stats/").then(r => r.data),
        enabled: isAuthenticated && hasPermission(user, 'stats', 'view')
    });

    const businessGetActiveBillingQuery = useQuery({
        queryKey: ["business", "get_active_billing"],
        queryFn: () => axiosPrivate.get("business/get_active_billing/").then(r => r.data),
        enabled: isAuthenticated
    });

    const businessGetBillingServicesQuery = useQuery({
        queryKey: ["business", "get_billing_services"],
        queryFn: () => axiosPrivate.get("business/get_billing_services/").then(r => r.data),
        enabled: isAuthenticated
    });

    const businessGetPlanFeaturesQuery = useQuery({
        queryKey: ["business", "get_plan_features"],
        queryFn: () => axiosPrivate.get("business/get_plan_features/").then(r => r.data),
        enabled: isAuthenticated
    });

    const businessGetCalendarSlugQuery = useQuery({
        queryKey: ["business", "get_calendar_slug"],
        queryFn: () => axiosPrivate.get("business/get_calendar_slug/").then(r => r.data),
        enabled: false,
    });

    // MUTATIONS
    const updateBusinessMutation = useMutation({
        mutationFn: ({id, data}) => axiosPrivate.patch(`business/${id}/`, data, {headers: {'Content-Type': 'multipart/form-data'}}).then(r => r.data),
        onSuccess: () => queryClient.invalidateQueries(["business"])
    });

    const addNewBillingService = useMutation({
        mutationFn: (data) => axiosPrivate.post("business/add_new_billing_service/", data).then(r => r.data),
        onSuccess: () => queryClient.invalidateQueries(["business"])
    });

    const setBillingServiceAsActive = useMutation({
        mutationFn: (data) => axiosPrivate.post("business/set_billing_service_as_active/", data).then(r => r.data),
        onSuccess: () => queryClient.invalidateQueries(["business"])
    });

    const deactivateBillingService = useMutation({
        mutationFn: (data) => axiosPrivate.post("business/deactivate_billing_service/", data).then(r => r.data),
        onSuccess: () => queryClient.invalidateQueries(["business"])
    });

    const deleteBillingDetail = useMutation({
        mutationFn: (data) => axiosPrivate.post("business/delete_billing_detail/", data).then(r => r.data),
        onSuccess: () => queryClient.invalidateQueries(["business"])
    });

    const editBillingService = useMutation({
        mutationFn: (data) => axiosPrivate.post("business/edit_billing_service/", data).then(r => r.data),
        onSuccess: () => queryClient.invalidateQueries(["business"])
    });

    return {
        // Queries
        businessGetBusinessQuery,
        businessGetSubscriptionInfoQuery,
        businessGetServiceStatsQuery,
        businessGetReservationStatsQuery,
        businessGetActiveBillingQuery,
        businessGetBillingServicesQuery,
        businessGetPlanFeaturesQuery,
        businessGetCalendarSlugQuery,

        // Mutations
        updateBusinessMutation,
        addNewBillingService,
        setBillingServiceAsActive,
        deactivateBillingService,
        deleteBillingDetail,
        editBillingService
    };
}
